/**
 * Perfume Swipe Discovery Engine - Complete Integrated Engine
 */
class PerfumeSwipeEngine {
  constructor(element) {
    this.section = element;
    this.deck = element.querySelector('.perfume-deck');
    this.bottomButtons = element.querySelector('.bottom-buttons');
    this.emptyState = element.querySelector('.perfume-swipe-empty-state');
    this.subtitle = element.querySelector('.perfume-swipe-subtitle');
    this.likedGrid = element.querySelector('.liked-perfumes-grid');
    this.drawer = element.querySelector('.perfume-drawer');
    
    // UI Counter element pointers
    this.favsCounterLabel = element.querySelector('.favs-counter-label');
    this.globalFavsCount = element.querySelector('.global-favs-count');
    this.midFavsBtn = element.querySelector('.mid-favs-btn');
    this.cornerFavsBtn = element.querySelector('.deck-favs-btn');
    
    this.sensitivity = parseInt(element.dataset.swipeSensitivity) || 120;
    this.speed = parseInt(element.dataset.animationSpeed) || 350;
    this.loopEnabled = element.dataset.loop === 'true';
    
    this.isDragging = false;
    this.startX = 0;
    this.startY = 0;
    this.currentX = 0;
    this.currentY = 0;

    // Save initial card count
    const baselineCards = this.deck.querySelectorAll('.perfume-card');
    this.initialTotalCardsCount = baselineCards.length;

    this.visitorSessionId = localStorage.getItem('perfume_session_id');
    if (!this.visitorSessionId) {
      this.visitorSessionId = 'user_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
      localStorage.setItem('perfume_session_id', this.visitorSessionId);
    }
    
    this.init();
  }

  init() {
    if (this.emptyState) {
      this.emptyState.style.setProperty('display', 'none', 'important');
    }

    this.loadStorageData();
    this.updateCounters();
    this.applyHistoryFilters();
    this.bindGlobalDragEvents();
  }

  loadStorageData() {
    this.swipedProductIds = JSON.parse(localStorage.getItem('perfume_swiped_ids')) || [];
    this.likedProducts = JSON.parse(localStorage.getItem('perfume_liked_products')) || [];
  }

  updateCounters() {
    const totalFavs = this.likedProducts.length;
    if (this.favsCounterLabel) this.favsCounterLabel.textContent = totalFavs;
    if (this.globalFavsCount) this.globalFavsCount.textContent = totalFavs;
  }

  applyHistoryFilters() {
    let dynamicCards = Array.from(this.deck.querySelectorAll('.perfume-card'));
    
    if (!this.loopEnabled && this.swipedProductIds.length > 0) {
      dynamicCards.forEach(card => {
        if (this.swipedProductIds.includes(card.dataset.productId)) {
          card.remove();
        }
      });
    }

    this.updateStackLayout();
    this.evaluateDeckStatus();
  }

  updateStackLayout() {
    this.activeCards = Array.from(this.deck.querySelectorAll('.perfume-card'));
    const swipedAwayCount = this.initialTotalCardsCount - this.activeCards.length;
    
    this.activeCards.forEach((card, index) => {
      card.classList.remove('card-1', 'card-2', 'card-3', 'card-hidden');
      
      if (index === 0) {
        card.classList.add('card-1');
        this.bindCardGestures(card);
      }
      else if (index === 1) card.classList.add('card-2');
      else if (index === 2) card.classList.add('card-3');
      else card.classList.add('card-hidden');
      
      const currentDisplayIndex = swipedAwayCount + index + 1;
      const counterLabel = card.querySelector('.card-counter');
      if (counterLabel) {
        counterLabel.textContent = `${currentDisplayIndex} / ${this.initialTotalCardsCount}`;
      }
    });

    this.bindButtonControls();
  }

  bindCardGestures(card) {
    card.addEventListener('mousedown', (e) => this.handleDragStart(e));
    card.addEventListener('touchstart', (e) => this.handleDragStart(e), { passive: false });
  }

  bindButtonControls() {
    const skipBtn = this.section.querySelector('.skip-btn');
    const likeBtn = this.section.querySelector('.like-btn');
    
    if (skipBtn && !skipBtn.dataset.bound) {
      skipBtn.dataset.bound = "true";
      skipBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.executeButtonSwipe('left');
      });
    }
    
    if (likeBtn && !likeBtn.dataset.bound) {
      likeBtn.dataset.bound = "true";
      likeBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.executeButtonSwipe('right');
      });
    }

    if (this.midFavsBtn && !this.midFavsBtn.dataset.bound) {
      this.midFavsBtn.dataset.bound = "true";
      this.midFavsBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.triggerFavoritesDashboardJump();
      });
    }

    if (this.cornerFavsBtn && !this.cornerFavsBtn.dataset.bound) {
      this.cornerFavsBtn.dataset.bound = "true";
      this.cornerFavsBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.triggerFavoritesDashboardJump();
      });
    }

    // Attach click events specifically to the View Details / Open Drawer button
    this.deck.querySelectorAll('.open-drawer-btn').forEach(btn => {
      if (!btn.dataset.bound) {
        btn.dataset.bound = "true";
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          const targetBtn = e.target.closest('.open-drawer-btn');
          const card = targetBtn ? targetBtn.closest('.perfume-card') : null;
          if (card || targetBtn) {
            const handle = targetBtn.dataset.productHandle || card?.dataset?.productHandle;
            this.openProductDrawer(handle, targetBtn);
          }
        });
      }
    });
  }

  triggerFavoritesDashboardJump() {
    this.activeCards.forEach(c => c.remove());
    this.updateStackLayout();
    this.evaluateDeckStatus();
    setTimeout(() => {
      this.emptyState.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  }

  bindGlobalDragEvents() {
    const closeBtn = this.drawer ? this.drawer.querySelector('.close-drawer-btn') : null;
    const overlay = this.drawer ? this.drawer.querySelector('.perfume-drawer-overlay') : null;
    
    if (closeBtn) closeBtn.addEventListener('click', () => this.closeProductDrawer());
    if (overlay) overlay.addEventListener('click', () => this.closeProductDrawer());
    
    document.addEventListener('mousemove', (e) => this.handleDragMove(e));
    document.addEventListener('mouseup', () => this.handleDragEnd());
    document.addEventListener('touchmove', (e) => this.handleDragMove(e), { passive: false });
    document.addEventListener('touchend', () => this.handleDragEnd());
  }

  handleDragStart(e) {
    if (e.target.closest('.bottom-buttons') || e.target.closest('.open-drawer-btn') || (this.drawer && this.drawer.getAttribute('aria-hidden') === 'false')) return;
    
    const topCard = this.getTopCard();
    if (!topCard || e.currentTarget !== topCard) return;

    this.isDragging = true;
    topCard.classList.add('swiping');
    
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    this.startX = clientX;
    this.startY = clientY;
    this.currentX = 0;
    this.currentY = 0;

    if (e.touches) e.stopPropagation();
  }

  handleDragMove(e) {
    if (!this.isDragging) return;
    const topCard = this.getTopCard();
    if (!topCard) return;

    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    this.currentX = clientX - this.startX;
    this.currentY = clientY - this.startY;

    const rotation = this.currentX / 15;
    
    topCard.style.setProperty('transform', `translate3d(${this.currentX}px, ${this.currentY}px, 0) rotate(${rotation}deg)`, 'important');

    const likeOverlay = topCard.querySelector('.swipe-overlay-like');
    const skipOverlay = topCard.querySelector('.swipe-overlay-skip');
    const opacityScale = Math.min(Math.abs(this.currentX) / this.sensitivity, 1);

    if (this.currentX > 0) {
      if (likeOverlay) likeOverlay.style.opacity = opacityScale;
      if (skipOverlay) skipOverlay.style.opacity = 0;
    } else {
      if (skipOverlay) skipOverlay.style.opacity = opacityScale;
      if (likeOverlay) likeOverlay.style.opacity = 0;
    }

    if (e.cancelable) e.preventDefault();
  }

  handleDragEnd() {
    if (!this.isDragging) return;
    this.isDragging = false;
    const topCard = this.getTopCard();
    if (!topCard) return;

    topCard.classList.remove('swiping');
    
    const likeOverlay = topCard.querySelector('.swipe-overlay-like');
    const skipOverlay = topCard.querySelector('.swipe-overlay-skip');

    if (this.currentX > this.sensitivity) {
      this.dismissCard(topCard, 'right');
    } else if (this.currentX < -this.sensitivity) {
      this.dismissCard(topCard, 'left');
    } else {
      topCard.style.transform = '';
      if (likeOverlay) likeOverlay.style.opacity = 0;
      if (skipOverlay) skipOverlay.style.opacity = 0;
    }
  }

  executeButtonSwipe(direction) {
    const topCard = this.getTopCard();
    if (!topCard) return;
    this.dismissCard(topCard, direction);
  }

  dismissCard(card, direction) {
    const pId = card.dataset.productId;
    const pUrl = card.dataset.productUrl;
    const pImg = card.querySelector('.perfume-image img')?.src || '';
    const pTitle = card.querySelector('.perfume-image img')?.alt || 'Perfume Scent';
    const actionType = direction === 'right' ? 'liked' : 'skipped';

    this.syncSwipeToDatabase(pId, pTitle, actionType);

    if (!this.swipedProductIds.includes(pId)) {
      this.swipedProductIds.push(pId);
      localStorage.setItem('perfume_swiped_ids', JSON.stringify(this.swipedProductIds));
    }

    if (direction === 'right') {
      const alreadyLiked = this.likedProducts.some(item => item.id === pId);
      if (!alreadyLiked) {
        this.likedProducts.push({ id: pId, title: pTitle, url: pUrl, image: pImg });
        localStorage.setItem('perfume_liked_products', JSON.stringify(this.likedProducts));
      }
    }

    this.updateCounters();

    const throwX = direction === 'right' ? window.innerWidth + 200 : -window.innerWidth - 200;
    card.style.setProperty('transition', `transform ${this.speed}ms cubic-bezier(0.25, 1, 0.5, 1), opacity ${this.speed}ms ease`, 'important');
    card.style.setProperty('transform', `translate3d(${throwX}px, ${this.currentY || 0}px, 0) rotate(${throwX / 10}deg)`, 'important');
    card.style.opacity = '0';

    setTimeout(() => {
      card.remove();
      this.updateStackLayout();
      this.evaluateDeckStatus();
    }, this.speed);
  }

  async syncSwipeToDatabase(productId, productTitle, actionType) {
    const mechanicWebhookUrl = "https://webhooks.mechanic.dev/6987f515-4fb7-4590-ae15-acd0acc91ae6"; 
    const payload = {
      sessionId: this.visitorSessionId,
      productId: productId,
      productTitle: productTitle,
      action: actionType,
      timestamp: new Date().toISOString()
    };
    try {
      fetch(mechanicWebhookUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
    } catch (e) { console.warn("Analytics storage offline:", e); }
  }

  // --- DRAWER DISCOVERY & RENDER ENGINE ---
  async openProductDrawer(handle, triggerBtn = null) {
    if (!handle || !this.drawer) return;
    this.drawer.setAttribute('aria-hidden', 'false');
    this.drawer.classList.add('is-active', 'is-loading');

    const spinner = this.drawer.querySelector('.drawer-loading-spinner');
    if (spinner) spinner.style.display = 'block';

    try {
      const response = await fetch(`/products/${handle}.js`);
      const product = await response.json();
      if (spinner) spinner.style.display = 'none';
      this.renderDrawerContent(product, triggerBtn);
    } catch (err) {
      if (spinner) spinner.style.display = 'none';
      const contentContainer = this.drawer.querySelector('.drawer-dynamic-content');
      if (contentContainer) {
        contentContainer.innerHTML = `<p style="padding:20px; text-align:center; color:#ef4444;">Unable to load perfume details right now.</p>`;
      }
    } finally {
      this.drawer.classList.remove('is-loading');
    }
  }

  closeProductDrawer() {
    if (!this.drawer) return;
    this.drawer.setAttribute('aria-hidden', 'true');
    this.drawer.classList.remove('is-active');
  }
renderDrawerContent(product) {
    const contentContainer = this.drawer.querySelector('.drawer-dynamic-content');
    if (!contentContainer) return;

    // Inject Clean Preview Drawer Structure (No Price, No Add to Cart)
    contentContainer.innerHTML = `
      <div class="drawer-product-media" style="text-align: center; margin-bottom: 15px;">
        <img id="DrawerProductImg" src="${product.featured_image || ''}" alt="${product.title}" style="max-height: 250px; width: auto; object-fit: contain;">
      </div>

      <h3 class="drawer-product-title" id="DrawerProductTitle" style="font-size: 1.3rem; font-weight: 700; color: #111827; margin-bottom: 12px; line-height: 1.3; text-align: center;">
        ${product.title}
      </h3>
      
      <!-- Product Description -->
      <div class="drawer-product-description" style="font-size: 0.9rem; color: #4b5563; line-height: 1.6; margin-bottom: 24px; max-height: 200px; overflow-y: auto; text-align: left;">
        ${product.description || 'No description available for this fragrance.'}
      </div>

      <!-- Direct Redirect Button -->
      <div style="margin-top: 15px; text-align: center;">
        <a href="${product.url}" class="view-product-page-btn" style="display: block; width: 100%; padding: 14px; background: #4ed8be; color: #ffffff; text-decoration: none; border-radius: 8px; font-weight: 700; font-size: 0.95rem; text-align: center; transition: background 0.2s ease;">
          View Product Page →
        </a>
      </div>
    `;
  }

  getTopCard() { 
    return this.activeCards.length ? this.activeCards[0] : null; 
  }

  evaluateDeckStatus() {
    if (this.activeCards.length === 0) {
      if (this.deck) this.deck.style.setProperty('display', 'none', 'important');
      if (this.bottomButtons) this.bottomButtons.style.setProperty('display', 'none', 'important');
      if (this.cornerFavsBtn) this.cornerFavsBtn.style.setProperty('display', 'none', 'important');
      if (this.subtitle) this.subtitle.style.setProperty('display', 'none', 'important');
      
      if (this.emptyState) {
        const emptyStateContainer = this.emptyState.querySelector('.empty-state-content');
        if (emptyStateContainer) {
          emptyStateContainer.innerHTML = `
            <h3>Your Fragrance Discovery Is Complete</h3>
            <div class="completion-actions-wrapper">
              <button type="button" class="reset-deck-btn outline-black-btn">Explore Scents Again</button>
              <button type="button" class="view-favorites-btn brand-filled-btn">View Your Favorites (${this.likedProducts.length})</button>
            </div>
            <div class="favorites-inline-drawer style-hidden" style="margin-top: 20px; display: none;">
              <div class="liked-perfumes-grid"></div>
            </div>
          `;

          emptyStateContainer.querySelector('.reset-deck-btn').addEventListener('click', () => this.resetDeckToStart());

          const favoritesDrawer = emptyStateContainer.querySelector('.favorites-inline-drawer');
          const gridContainer = emptyStateContainer.querySelector('.liked-perfumes-grid');
          const viewFavsBtn = emptyStateContainer.querySelector('.view-favorites-btn');

          viewFavsBtn.addEventListener('click', () => {
            if (favoritesDrawer.classList.contains('style-hidden')) {
              gridContainer.innerHTML = '';
              if (this.likedProducts.length === 0) {
                gridContainer.innerHTML = `<div style="grid-column: span 2; color: rgba(0,0,0,0.4); font-size: 0.85rem; padding: 10px;">No fragrances matched this session.</div>`;
              } else {
                this.likedProducts.forEach(prod => {
                  gridContainer.innerHTML += `
                    <div class="liked-item-card" data-id="${prod.id}">
                      <img src="${prod.image}" alt="${prod.title}">
                      <div class="liked-item-title">${prod.title}</div>
                      <a href="${prod.url}" class="liked-item-link">View Details</a>
                      <button type="button" class="unlike-item-btn">✕ Remove</button>
                    </div>
                  `;
                });
              }

              gridContainer.querySelectorAll('.unlike-item-btn').forEach(btn => {
                btn.addEventListener('click', (e) => {
                  const itemCard = e.target.closest('.liked-item-card');
                  const pId = itemCard.dataset.id;
                  const pTitle = itemCard.querySelector('.liked-item-title').textContent;

                  this.likedProducts = this.likedProducts.filter(item => item.id !== pId);
                  localStorage.setItem('perfume_liked_products', JSON.stringify(this.likedProducts));
                  this.syncSwipeToDatabase(pId, pTitle, 'unliked');

                  itemCard.remove();
                  viewFavsBtn.textContent = `View Your Favorites (${this.likedProducts.length})`;
                  this.updateCounters();
                  if (this.likedProducts.length === 0) {
                    gridContainer.innerHTML = `<div style="grid-column: span 2; color: rgba(0,0,0,0.4); font-size: 0.85rem; padding: 10px;">No fragrances matched this session.</div>`;
                  }
                });
              });

              favoritesDrawer.classList.remove('style-hidden');
              favoritesDrawer.style.setProperty('display', 'block', 'important');
            } else {
              favoritesDrawer.classList.add('style-hidden');
              favoritesDrawer.style.setProperty('display', 'none', 'important');
            }
          });
        }
        this.emptyState.style.setProperty('display', 'block', 'important');
      }
    } else {
      if (this.deck) this.deck.style.setProperty('display', 'flex', 'important');
      if (this.bottomButtons) this.bottomButtons.style.setProperty('display', 'flex', 'important');
      if (this.cornerFavsBtn) this.cornerFavsBtn.style.setProperty('display', 'flex', 'important');
      if (this.subtitle) this.subtitle.style.setProperty('display', 'block', 'important');
      if (this.emptyState) this.emptyState.style.setProperty('display', 'none', 'important');
    }
  }

  resetDeckToStart() {
    localStorage.removeItem('perfume_swiped_ids');
    localStorage.removeItem('perfume_liked_products');
    this.loadStorageData();
    if (this.deck) this.deck.innerHTML = '';
    window.location.reload();
  }
}

// Auto-initialize when document is ready
document.addEventListener('DOMContentLoaded', () => {
  const swipeSection = document.querySelector('.perfume-swipe-section');
  if (swipeSection) new PerfumeSwipeEngine(swipeSection);
});